<template>
  <div class="order-step">
    <div class="two-col-res">
      <div>
        <div
          class="card"
          v-if="
            !hasInvoiceAddress &&
            !this.custom.addresses.newInvoice &&
            addresses.length === 0
          "
        >
          <p class="missing-invoice">
            Uw factuurgegevens ontbreken nog.<br />
            <button class="new-address pill-button" @click="addCustomer()">
              Factuurgegevens toevoegen
            </button>
          </p>
        </div>
        <div class="card" v-else>
          <div class="card-selection">
            <div class="list-container list-left list-header">
              <address-list
                :currentAddress="address"
                :type="orderType"
                title="Klantnummer"
                @setAddress="setAddress"
                @setNewCustomer="setNewCustomer"
              ></address-list>
            </div>

            <div class="selection-main">
              <div class="title-container">
                <h3>Factuurgegevens</h3>
                <div class="button-holder">
                  <button
                    class="white-pill-s"
                    @click="editCustomer"
                    v-if="address.newCustomer"
                  >
                    Bewerken
                  </button>
                </div>
              </div>

              <customer-blocked-message v-if="address.blocked" />

              <div v-else>
                <div class="address">
                  <p
                    v-if="!hasInvoiceAddress && addresses.length > 0"
                    class="error-text"
                  >
                    Selecteer links een klantnummer bij deze order
                  </p>
                  <p v-else-if="address.newCustomer && !address.company">
                    Klantnummer: {{ address.customerNumber }}
                  </p>
                  <div v-else-if="address.newCustomer">
                    <p>{{ address.company }}</p>
                    <p v-if="address.name">T.a.v. {{ address.name }}</p>
                    <p>
                      {{ address.street }} {{ address.number }}
                      {{ address.addition }}
                    </p>
                    <p>{{ address.zipCode }} {{ address.city }}</p>
                  </div>

                  <div v-else>
                    <p>
                      <span class="bold">Klantnummer:</span>
                      {{ address.customerNumber }}
                    </p>
                    <p>
                      <span class="bold">Bedrijfsnaam:</span>
                      {{ address.company }}
                    </p>
                  </div>
                </div>

                <labeled-project-field
                  v-if="hasInvoiceAddress"
                  :label="custom.project.label || 'Project'"
                  input-id="projectname"
                  holder-id="searchproj"
                  :show-list="
                    !address.newCustomer && !custom.project.useFormatString
                  "
                  @holder-click="projectAction"
                  @show-list="showProjectList"
                >
                  <project-input
                    :fields="custom.project.fields"
                    :hyphen="custom.project.hyphen"
                    :prefix="custom.project.prefix"
                    :project-name="orderDetails.project.name"
                    :is-required="projectRequired"
                    :is-readonly="custom.project.readonly"
                    @setProject="setProject"
                  ></project-input>
                </labeled-project-field>

                <labeled-project-field
                  v-if="!custom.reference.hide && hasInvoiceAddress"
                  :label="custom.reference.label || 'Orderreferentie'"
                  input-id="factref"
                  :show-list="!address.newCustomer"
                  @show-list="showReferenceList"
                >
                  <project-input
                    :fields="[
                      {
                        format: custom.reference.format || '',
                        maxLength: custom.reference.maxLength || 30,
                        placeholder:
                          custom.reference.placeholder || 'max 30 karakters'
                      }
                    ]"
                    :project-name="orderDetails.reference"
                    :is-required="custom.reference.required"
                    @setProject="setReference"
                  ></project-input>
                </labeled-project-field>

                <labeled-project-field
                  v-if="custom.externalId?.show && hasInvoiceAddress"
                  :label="custom.externalId.label || 'Externe ID'"
                  input-id="externalid"
                >
                  <project-input
                    :fields="[
                      {
                        format: custom.externalId.format || '',
                        maxLength: custom.externalId.maxLength || 32,
                        placeholder: custom.externalId.placeholder || ''
                      }
                    ]"
                    :prefix="custom.externalId.prefix"
                    :project-name="orderDetails.externalId"
                    :is-required="custom.externalId.required"
                    @setProject="setExternalId"
                  ></project-input>
                </labeled-project-field>

                <div class="offer" v-if="!hideQuote && hasInvoiceAddress">
                  <quotation
                    :quote-no="quotationNo"
                    @set-quote="setQuote"
                  ></quotation>
                </div>

                <slot name="footer"></slot>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="!hideSub">
        <div class="card card-front sub-card">
          <printstore-sidebar-ad
            v-if="showPrintStoreAd"
          ></printstore-sidebar-ad>
          <div v-else class="card-default">
            <address-card :address="shop"></address-card>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Invoiceaddress component
 */

import AddressSelectionList from '@/components/AddressSelectionList.vue'
import ProjectInput from '@/components/ProjectInput.vue'
import cloneDeep from 'lodash-es/cloneDeep'
import AddressCard from '@/components/AddressCard'

import PrintstoreSidebarAd from '@/components/PrintstoreSidebarAd.vue'

import AddNewCustomer from '@/dialogs/AddNewCustomer'

import AddressProjectList from '@/dialogs/AddressProjectList.vue'
import AddressReferenceList from '@/dialogs/AddressReferenceList'
import LabeledProjectField from '@/components/LabeledProjectField.vue'
import Quotation from '@/components/Quotation.vue'
import CustomerBlockedMessage from '@/components/CustomerBlockedMessage.vue'

import { useAddressBookStore } from '@/stores/AddressBook.store'
import { mapStores } from 'pinia'
import { add } from 'date-fns'

export default {
  props: {
    orderDetails: {},
    shop: {
      default() {
        return {}
      }
    },
    hideSub: { default: false },
    hideQuote: { default: false },
    projectOptional: { default: false }
  },
  data() {
    return {
      cardBack: '',
      addresses: [],
      orderType: 'invoice',
      editType: '',
      projectFormat: new RegExp(),
      newProjectInvalid: false,
      newReferenceInvalid: false
    }
  },
  components: {
    AddressList: AddressSelectionList,
    AddressCard,
    ProjectInput,
    PrintstoreSidebarAd,
    Quotation,
    LabeledProjectField,
    CustomerBlockedMessage
  },
  methods: {
    addCustomer() {
      this.$modal.show(
        AddNewCustomer,
        {
          callback: (address) => {
            this.setNewCustomer(address)
          }
        },
        { width: 445, height: 'auto' }
      )
    },
    editCustomer() {
      this.$modal.show(
        AddNewCustomer,
        {
          address: this.address,
          type: 'edit',
          callback: (address) => {
            this.setNewCustomer(address)
          }
        },
        { width: 445, height: 'auto' }
      )
    },
    setQuote(val) {
      this.updateOrder('quotationNo', val)
    },
    updateOrder(key, val) {
      this.$emit('updateOrder', [key, val])
    },
    updateProject(val) {
      this.$emit('updateProject', val)
    },
    setNewCustomer(address) {
      this.addressBookStore.saveNewInvoiceAddress(address)
      this.updateOrder('invoiceAddress', address)
    },
    setAddress(address) {
      if (
        this.orderDetails.invoiceAddress.customerNumber ===
        address.customerNumber
      ) {
        return
      }

      let selectedAddr = cloneDeep(address)

      //project
      if (this.formatString) {
        this.updateProject({
          name: this.formatString,
          id: 0
        })
      } else {
        if (address.projects?.length === 1) {
          this.updateProject({
            name: address.projects[0].name,
            id: address.projects[0].id
          })
        } else {
          this.updateProject({
            name: '',
            id: 0
          })
        }
      }

      //reference

      if (address.references?.length === 1) {
        this.updateOrder('reference', address.references[0].value)
      } else {
        this.updateOrder('reference', '')
      }

      delete selectedAddr.project
      delete selectedAddr.reference
      this.updateOrder('invoiceAddress', selectedAddr)
    },
    setProject(project) {
      let newProject = this.formatString ? this.formatString : project
      this.updateProject({
        name: newProject,
        id: 0
      })
    },
    setReference(reference) {
      this.updateOrder('reference', reference)
    },
    setExternalId(externalId) {
      this.updateOrder('externalId', externalId)
    },
    showProjectList() {
      this.$modal.show(
        AddressProjectList,
        {
          customerNumber: this.address.customerNumber,
          setProject: (project) => {
            this.updateProject(project)
          }
        },
        { width: 400, height: 'auto' }
      )
    },
    showReferenceList() {
      this.$modal.show(
        AddressReferenceList,
        {
          customerNumber: this.address.customerNumber,
          setReference: (reference) => {
            this.updateOrder('reference', reference)
          }
        },
        { width: 400, scrollable: true, height: 'auto' }
      )
    },
    projectAction() {
      if (this.formatString) {
        return
      } else if (this.custom.project.readonly) {
        this.showProjectList()
      } else {
        return
      }
    }
  },
  computed: {
    formatString() {
      return this.rootStore.user.formatString || ''
    },
    ...mapStores(useAddressBookStore),
    custom() {
      return this.rootStore.theme.customFields
    },
    projectRequired() {
      if (this.projectOptional) {
        return false
      } else {
        return !!(
          this.custom.project.required || this.rootStore.user.projectRequired
        )
      }
    },
    showPrintStoreAd() {
      return (
        this.rootStore.interactions.some(
          (ia) => ia.destination === 'printStore'
        ) && !this.rootStore.theme.customFields.printStoreAd.hideAds
      )
    },
    address() {
      return this.orderDetails.invoiceAddress
    },
    quotationNo() {
      return this.orderDetails.quotationNo
    },
    hasInvoiceAddress() {
      return this.address.customerNumber || this.address.company
    }
  },
  created() {
    this.addresses = this.addressBookStore.invoiceAddresses
  }
}
</script>

<style lang="scss" scoped>
.missing-invoice {
  text-align: center;
  padding-top: 170px;
}
.pill-button {
  background-color: var(--arch-color);
}
.new-address {
  margin-top: 20px;
}
.list-header {
  padding-top: 15px;
}
.card {
  height: 590px;
}
.address {
  line-height: 30px;
  padding-bottom: 5px;
  //line-height: 1.5rem;
}

.address-form {
  label {
    min-width: 70px;
  }
}

.iso-text {
  margin-top: 5px;
  flex-grow: 1;
  img {
    height: 51px;
    margin-top: 13px;
    margin-right: 13px;
  }
}

.offer {
  height: 89px;
  line-height: 36px;
}
</style>
